import { supabase } from '../lib/supabase';

// Helper function to clean date values
const cleanDateValue = (dateValue) => {
  if (!dateValue || dateValue === '' || dateValue === 'undefined' || dateValue === 'null') {
    return null;
  }
  return dateValue;
};

// Helper function to map payment channel to valid database values
const mapPaymentChannel = (channel) => {
  const validChannels = ['transfer', 'cash', 'check'];
  if (!channel || !validChannels.includes(channel)) {
    return 'transfer'; // default value
  }
  return channel;
};

// ฟังก์ชันสำหรับดึงข้อมูลโปรเจคทั้งหมดของผู้ใช้
export const getProjects = async () => {
  try {
    // ดึงข้อมูลโปรเจค
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (projectsError) throw projectsError;

    // ดึงข้อมูล payment installments
    const { data: payments, error: paymentsError } = await supabase
      .from('payment_installments')
      .select('*');

    if (paymentsError) throw paymentsError;

    // ดึงข้อมูล developer payments
    const { data: developers, error: developersError } = await supabase
      .from('developer_payments')
      .select('*');

    if (developersError) throw developersError;

    // ดึงข้อมูล other expenses
    const { data: otherExpensesData, error: otherExpensesError } = await supabase
      .from('other_expenses')
      .select('*');

    if (otherExpensesError) throw otherExpensesError;

    // รวมข้อมูล
    const transformedData = projects?.map(project => {
      // ใช้ข้อมูลจาก tables แยกเป็นหลัก
      const projectPayments = payments?.filter(payment => payment.project_id === project.id) || [];
      const projectDevelopers = developers?.filter(dev => dev.project_id === project.id) || [];
      const projectOtherExpenses = otherExpensesData?.filter(expense => expense.project_id === project.id) || [];
      
      // ถ้าไม่มีข้อมูลใน tables แยก ให้ใช้ข้อมูลจาก JSON fields ใน projects table
      let payment_schedule = projectPayments;
      let developer_payments = projectDevelopers;
      let other_expenses = projectOtherExpenses;
      
      // ถ้าไม่มีข้อมูลใน payment_installments table ให้ใช้ข้อมูลจาก payment_schedule JSON field
      if (payment_schedule.length === 0 && project.payment_schedule) {
        try {
          payment_schedule = typeof project.payment_schedule === 'string' 
            ? JSON.parse(project.payment_schedule) 
            : project.payment_schedule;
        } catch (e) {
          console.error('Error parsing payment_schedule:', e);
          payment_schedule = [];
        }
      }
      
      // ถ้าไม่มีข้อมูลใน developer_payments table ให้ใช้ข้อมูลจาก developer_payments JSON field
      if (developer_payments.length === 0 && project.developer_payments) {
        try {
          developer_payments = typeof project.developer_payments === 'string' 
            ? JSON.parse(project.developer_payments) 
            : project.developer_payments;
        } catch (e) {
          console.error('Error parsing developer_payments:', e);
          developer_payments = [];
        }
      }
      
      // ถ้าไม่มีข้อมูลใน other_expenses table ให้ใช้ข้อมูลจาก other_expenses JSON field
      if (other_expenses.length === 0 && project.other_expenses) {
        try {
          other_expenses = typeof project.other_expenses === 'string' 
            ? JSON.parse(project.other_expenses) 
            : project.other_expenses;
        } catch (e) {
          console.error('Error parsing other_expenses:', e);
          other_expenses = [];
        }
      }
      
      // แปลงข้อมูล payment_schedule ให้ตรงกับ form structure
      const transformedPaymentSchedule = payment_schedule.map(payment => ({
        id: payment.id,
        installment: payment.installment_number,
        installment_number: payment.installment_number,
        type: payment.type,
        amount: payment.amount,
        installment_amount: payment.amount,
        percentage: payment.percentage,
        due_date: payment.due_date,
        payment_date: payment.due_date,
        paid: payment.paid,
        paid_at: payment.paid_at,
        channel: payment.payment_channel,
        payment_channel: payment.payment_channel
      }));
      
      // แปลงข้อมูล developer_payments ให้ตรงกับ form structure
      const transformedDeveloperPayments = developer_payments.map(dev => ({
        id: dev.id,
        name: dev.developer_name,
        developer_name: dev.developer_name,
        amount: dev.amount,
        percentage: dev.percentage,
        type: dev.type,
        payment_mode: dev.payment_mode,
        withholding_tax: dev.withholding_tax,
        withholding_tax_percentage: typeof dev.withholding_tax === 'object' ? dev.withholding_tax.percentage : dev.withholding_tax,
        net_amount: dev.net_amount,
        payment_date: dev.payment_date,
        paid: dev.paid,
        channel: dev.payment_channel,
        payment_channel: dev.payment_channel
      }));
      
      // แปลงข้อมูล other_expenses ให้ตรงกับ form structure
      const transformedOtherExpenses = other_expenses.map(expense => ({
        id: expense.id,
        name: expense.expense_name,
        expense_name: expense.expense_name,
        amount: expense.amount,
        description: expense.description,
        date: expense.expense_date,
        expense_date: expense.expense_date,
        paid: expense.paid,
        paid_at: expense.paid_at,
        channel: expense.payment_channel,
        payment_channel: expense.payment_channel
      }));
      
      // คำนวณข้อมูลที่จำเป็นสำหรับการแสดงผล
      const totalPaidAmount = transformedPaymentSchedule
        .filter(payment => payment.paid)
        .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
      
      const totalPaymentAmount = transformedPaymentSchedule
        .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
      
      // คำนวณรายได้จริง (ยอดรับโอน = มูลค่ารวม - WHT)
      let actualReceivedAmount = 0;
      try {
        actualReceivedAmount = transformedPaymentSchedule
          .filter(payment => payment.paid)
          .reduce((sum, payment) => {
            const paymentAmount = parseFloat(payment.amount) || 0;
            
            // ยอดรับโอน = มูลค่ารวม - WHT
            // WHT คำนวณจากมูลค่าไม่รวม VAT
            const amountWithoutVat = paymentAmount / 1.07;
            const whtAmount = amountWithoutVat * 0.03; // WHT 3%
            const actualReceived = paymentAmount - whtAmount;
            return sum + actualReceived;
          }, 0);
      } catch (e) {
        console.error('Error calculating actual received amount:', e);
        actualReceivedAmount = totalPaidAmount; // fallback
      }
      
      const totalDevPaymentsAmount = transformedDeveloperPayments
        .reduce((sum, dev) => sum + (parseFloat(dev.amount) || 0), 0);
      
      const totalOtherExpensesAmount = transformedOtherExpenses
        .reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0);
      
      // นับจำนวนงวดที่ชำระแล้วและทั้งหมด
      const paidInstallmentsCount = transformedPaymentSchedule.filter(payment => payment.paid).length;
      const totalInstallmentsCount = transformedPaymentSchedule.length;
      
      // คำนวณ company fee และ sales commission ใหม่ (ให้สอดคล้องกับ form)
      let companyFeeAmount = 0;
      let salesCommissionAmount = 0;
      
      try {
        const companyFee = typeof project.company_fee === 'string' 
          ? JSON.parse(project.company_fee) 
          : project.company_fee;
        
        // คำนวณจากราคารวม VAT เหมือนกับ form
        if (companyFee?.type === 'percentage') {
          companyFeeAmount = (totalPaymentAmount * (companyFee.percentage || 0)) / 100;
        } else {
          companyFeeAmount = companyFee?.amount || 0;
        }
      } catch (e) {
        console.error('Error parsing company_fee:', e);
      }
      
      try {
        const salesCommission = typeof project.sales_commission === 'string' 
          ? JSON.parse(project.sales_commission) 
          : project.sales_commission;
        
        // คำนวณจากราคารวม VAT เหมือนกับ form
        if (salesCommission?.type === 'percentage') {
          salesCommissionAmount = (totalPaymentAmount * (salesCommission.percentage || 0)) / 100;
        } else {
          salesCommissionAmount = salesCommission?.amount || 0;
        }
      } catch (e) {
        console.error('Error parsing sales_commission:', e);
      }
      
      console.log(`Processing project ${project.project_name}:`, {
        projectId: project.id,
        payments: transformedPaymentSchedule,
        developers: transformedDeveloperPayments,
        otherExpenses: transformedOtherExpenses,
        totalPaidAmount,
        totalPaymentAmount,
        actualReceivedAmount,
        totalDevPaymentsAmount,
        totalOtherExpensesAmount,
        companyFeeAmount,
        salesCommissionAmount
      });
      
      return {
        ...project,
        payment_schedule: transformedPaymentSchedule,
        developer_payments: transformedDeveloperPayments,
        other_expenses: transformedOtherExpenses,
        // เพิ่ม calculated fields สำหรับการแสดงผล
        totalPaidAmount,
        totalPaymentAmount,
        actualReceivedAmount,
        totalDevPaymentsAmount,
        totalOtherExpensesAmount,
        companyFeeAmount,
        salesCommissionAmount,
        paidInstallmentsCount,
        totalInstallmentsCount,
        // คำนวณ profit
        profit: actualReceivedAmount - totalDevPaymentsAmount - totalOtherExpensesAmount - companyFeeAmount - salesCommissionAmount
      };
    });
    
    return { data: transformedData, error: null };
  } catch (error) {
    console.error('Error fetching projects:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับดึงข้อมูลโปรเจคตาม ID
export const getProjectById = async (id) => {
  try {
    // ดึงข้อมูลโปรเจค
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single();

    if (projectError) throw projectError;

    // ดึงข้อมูล payment installments
    const { data: payments, error: paymentsError } = await supabase
      .from('payment_installments')
      .select('*')
      .eq('project_id', id);

    if (paymentsError) throw paymentsError;

    // ดึงข้อมูล developer payments
    const { data: developers, error: developersError } = await supabase
      .from('developer_payments')
      .select('*')
      .eq('project_id', id);

    if (developersError) throw developersError;

    // ดึงข้อมูล other expenses
    const { data: otherExpenses, error: otherExpensesError } = await supabase
      .from('other_expenses')
      .select('*')
      .eq('project_id', id);

    if (otherExpensesError) throw otherExpensesError;

    // รวมข้อมูล
    console.log(`Loading project ${project.project_name} (${id}):`, {
      project: project,
      payments: payments,
      developers: developers,
      otherExpenses: otherExpenses
    });
    
    // ใช้ข้อมูลจาก tables แยกเป็นหลัก
    let payment_schedule = payments || [];
    let developer_payments = developers || [];
    let other_expenses = otherExpenses || [];
    
    // ถ้าไม่มีข้อมูลใน payment_installments table ให้ใช้ข้อมูลจาก payment_schedule JSON field
    if (payment_schedule.length === 0 && project.payment_schedule) {
      try {
        payment_schedule = typeof project.payment_schedule === 'string' 
          ? JSON.parse(project.payment_schedule) 
          : project.payment_schedule;
      } catch (e) {
        console.error('Error parsing payment_schedule:', e);
        payment_schedule = [];
      }
    }
    
    // ถ้าไม่มีข้อมูลใน developer_payments table ให้ใช้ข้อมูลจาก developer_payments JSON field
    if (developer_payments.length === 0 && project.developer_payments) {
      try {
        developer_payments = typeof project.developer_payments === 'string' 
          ? JSON.parse(project.developer_payments) 
          : project.developer_payments;
      } catch (e) {
        console.error('Error parsing developer_payments:', e);
        developer_payments = [];
      }
    }
    
    // ถ้าไม่มีข้อมูลใน other_expenses table ให้ใช้ข้อมูลจาก other_expenses JSON field
    if (other_expenses.length === 0 && project.other_expenses) {
      try {
        other_expenses = typeof project.other_expenses === 'string' 
          ? JSON.parse(project.other_expenses) 
          : project.other_expenses;
      } catch (e) {
        console.error('Error parsing other_expenses:', e);
        other_expenses = [];
      }
    }
    
    // แปลงข้อมูล payment_schedule ให้ตรงกับ form structure
    const transformedPaymentSchedule = payment_schedule.map(payment => ({
      id: payment.id,
      installment: payment.installment_number,
      installment_number: payment.installment_number,
      type: payment.type,
      amount: payment.amount,
      installment_amount: payment.amount,
      percentage: payment.percentage,
      due_date: payment.due_date,
      payment_date: payment.due_date,
      paid: payment.paid,
      paid_at: payment.paid_at,
      channel: payment.payment_channel,
      payment_channel: payment.payment_channel
    }));
    
    // แปลงข้อมูล developer_payments ให้ตรงกับ form structure
    const transformedDeveloperPayments = developer_payments.map(dev => ({
      id: dev.id,
      name: dev.developer_name,
      developer_name: dev.developer_name,
      amount: dev.amount,
      percentage: dev.percentage,
      type: dev.type,
      payment_mode: dev.payment_mode,
      withholding_tax: dev.withholding_tax,
      withholding_tax_percentage: typeof dev.withholding_tax === 'object' ? dev.withholding_tax.percentage : dev.withholding_tax,
      net_amount: dev.net_amount,
      payment_date: dev.payment_date,
      paid: dev.paid,
      channel: dev.payment_channel,
      payment_channel: dev.payment_channel
    }));
    
    // แปลงข้อมูล other_expenses ให้ตรงกับ form structure
    const transformedOtherExpenses = other_expenses.map(expense => ({
      id: expense.id,
      name: expense.expense_name,
      expense_name: expense.expense_name,
      amount: expense.amount,
      description: expense.description,
      date: expense.expense_date,
      expense_date: expense.expense_date,
      paid: expense.paid,
      paid_at: expense.paid_at,
      channel: expense.payment_channel,
      payment_channel: expense.payment_channel
    }));
    
    // คำนวณข้อมูลที่จำเป็นสำหรับการแสดงผล
    const totalPaidAmount = transformedPaymentSchedule
      .filter(payment => payment.paid)
      .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    
    const totalPaymentAmount = transformedPaymentSchedule
      .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    
    // คำนวณรายได้จริง (ยอดรับโอน = มูลค่ารวม - WHT)
    let actualReceivedAmount = 0;
    try {
      actualReceivedAmount = transformedPaymentSchedule
        .filter(payment => payment.paid)
        .reduce((sum, payment) => {
          const paymentAmount = parseFloat(payment.amount) || 0;
          
          // ยอดรับโอน = มูลค่ารวม - WHT
          // WHT คำนวณจากมูลค่าไม่รวม VAT
          const amountWithoutVat = paymentAmount / 1.07;
          const whtAmount = amountWithoutVat * 0.03; // WHT 3%
          const actualReceived = paymentAmount - whtAmount;
          return sum + actualReceived;
        }, 0);
    } catch (e) {
      console.error('Error calculating actual received amount:', e);
      actualReceivedAmount = totalPaidAmount; // fallback
    }
    
    const totalDevPaymentsAmount = transformedDeveloperPayments
      .reduce((sum, dev) => sum + (parseFloat(dev.amount) || 0), 0);
    
    const totalOtherExpensesAmount = transformedOtherExpenses
      .reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0);
    
    // นับจำนวนงวดที่ชำระแล้วและทั้งหมด
    const paidInstallmentsCount = transformedPaymentSchedule.filter(payment => payment.paid).length;
    const totalInstallmentsCount = transformedPaymentSchedule.length;
    
    // คำนวณ company fee และ sales commission ใหม่ (ให้สอดคล้องกับ form)
    let companyFeeAmount = 0;
    let salesCommissionAmount = 0;
    
    try {
      const companyFee = typeof project.company_fee === 'string' 
        ? JSON.parse(project.company_fee) 
        : project.company_fee;
      
      // คำนวณจากราคารวม VAT เหมือนกับ form
      if (companyFee?.type === 'percentage') {
        companyFeeAmount = (totalPaymentAmount * (companyFee.percentage || 0)) / 100;
      } else {
        companyFeeAmount = companyFee?.amount || 0;
      }
    } catch (e) {
      console.error('Error parsing company_fee:', e);
    }
    
    try {
      const salesCommission = typeof project.sales_commission === 'string' 
        ? JSON.parse(project.sales_commission) 
        : project.sales_commission;
      
      // คำนวณจากราคารวม VAT เหมือนกับ form
      if (salesCommission?.type === 'percentage') {
        salesCommissionAmount = (totalPaymentAmount * (salesCommission.percentage || 0)) / 100;
      } else {
        salesCommissionAmount = salesCommission?.amount || 0;
      }
    } catch (e) {
      console.error('Error parsing sales_commission:', e);
    }
    
    const transformedData = {
      ...project,
      // Field names ที่ตรงกับ form structure
      payment_schedule: transformedPaymentSchedule,
      developer_payments: transformedDeveloperPayments,
      other_expenses: transformedOtherExpenses,
      // เพิ่ม calculated fields สำหรับการแสดงผล
      totalPaidAmount,
      totalPaymentAmount,
      actualReceivedAmount,
      totalDevPaymentsAmount,
      totalOtherExpensesAmount,
      companyFeeAmount,
      salesCommissionAmount,
      paidInstallmentsCount,
      totalInstallmentsCount,
      // คำนวณ profit
      profit: actualReceivedAmount - totalDevPaymentsAmount - totalOtherExpensesAmount - companyFeeAmount - salesCommissionAmount
    };
    
    return { data: transformedData, error: null };
  } catch (error) {
    console.error('Error fetching project:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับสร้างโปรเจคใหม่
export const createProject = async (projectData) => {
  try {
    console.log('createProject called with data:', projectData);
    
    // กรองข้อมูลที่ไม่ควรส่งไปยัง projects table
    const { 
      payment_schedule, 
      developer_payments,
      other_expenses,
      // กรอง calculated fields ที่ไม่มีใน database schema
      priceBeforeVat,
      vatAmount,
      finalAmount,
      withholdingTaxAmount,
      actualReceived,
      totalCompanyFeeAmount,
      totalSalesCommissionAmount,
      totalOtherExpensesAmount,
      remainingBeforeDev,
      totalDevPaymentsAmount,
      discountAmount, // เพิ่มฟิลด์นี้
      discountPercentage, // เพิ่มฟิลด์นี้
      projectPrice, // เพิ่มฟิลด์นี้
      priceAfterDiscount, // เพิ่มฟิลด์นี้
      vatPercentage, // เพิ่มฟิลด์นี้
      withholdingTaxPercentage, // เพิ่มฟิลด์นี้
      ...projectFields 
    } = projectData;

    console.log('Project fields to insert:', projectFields);
    console.log('Payment schedule data:', payment_schedule);
    console.log('Developer payments data:', developer_payments);
    console.log('Other expenses data:', other_expenses);
    
    // คำนวณค่า calculated fields ก่อนบันทึก
    const calculatedProjectPrice = parseFloat(projectFields.project_price) || 0;
    const vatEnabled = projectFields.vat?.enabled || false;
    const calculatedVatPercentage = projectFields.vat?.percentage || 0;
    const withholdingTaxEnabled = projectFields.withholding_tax?.enabled || false;
    const withholdingTaxConfig = projectFields.withholding_tax || {};
    
    // คำนวณส่วนลด
    const discount = projectFields.discount || {};
    let calculatedDiscountAmount = 0;
    if (discount.enabled) {
      if (discount.type === 'percentage') {
        calculatedDiscountAmount = (calculatedProjectPrice * (discount.percentage || 0)) / 100;
      } else {
        calculatedDiscountAmount = discount.amount || 0;
      }
    }
    
    // คำนวณราคาหลังหักส่วนลด
    const calculatedPriceAfterDiscount = calculatedProjectPrice - calculatedDiscountAmount;
    
    // คำนวณราคาไม่รวม VAT และราคารวม VAT ตาม price_type
    let calculatedPriceBeforeVat = 0;
    let totalWithVat = 0;
    let calculatedVatAmount = 0;
    
    if (projectFields.price_type === 'include_vat') {
      // ราคารวม VAT แล้ว - แยก VAT ออกมา
      totalWithVat = calculatedPriceAfterDiscount;
      calculatedPriceBeforeVat = vatEnabled ? totalWithVat / (1 + (calculatedVatPercentage / 100)) : totalWithVat;
      calculatedVatAmount = vatEnabled ? totalWithVat - calculatedPriceBeforeVat : 0;
    } else {
      // ราคาไม่รวม VAT - คำนวณ VAT เพิ่ม
      calculatedPriceBeforeVat = calculatedPriceAfterDiscount;
      calculatedVatAmount = vatEnabled ? (calculatedPriceBeforeVat * calculatedVatPercentage) / 100 : 0;
      totalWithVat = calculatedPriceBeforeVat + calculatedVatAmount;
    }
    
    // คำนวณ WHT ตามประเภทที่เลือก
    let calculatedWithholdingTaxAmount = 0;
    if (withholdingTaxEnabled) {
      if (withholdingTaxConfig.type === 'amount') {
        // ใช้จำนวนเงินที่กรอกโดยตรง
        calculatedWithholdingTaxAmount = withholdingTaxConfig.amount || 0;
      } else {
        // คำนวณจากเปอร์เซ็นต์
        const withholdingTaxPercentage = withholdingTaxConfig.percentage || 0;
        calculatedWithholdingTaxAmount = (calculatedPriceBeforeVat * withholdingTaxPercentage / 100);
      }
    }
    
    // คำนวณยอดรับจริง (ราคารวม VAT - WHT)
    const calculatedActualReceived = totalWithVat - calculatedWithholdingTaxAmount;
    
    // คำนวณค่าบริการบริษัท (ใช้ราคารวม VAT เหมือนกับ form)
    const companyFee = projectFields.company_fee || {};
    let companyFeeAmount = 0;
    if (companyFee.type === 'percentage') {
      companyFeeAmount = (totalWithVat * (companyFee.percentage || 0)) / 100;
    } else {
      companyFeeAmount = companyFee.amount || 0;
    }
    
    // คำนวณค่าคอมมิชชั่น (ใช้ราคารวม VAT เหมือนกับ form)
    const salesCommission = projectFields.sales_commission || {};
    let salesCommissionAmount = 0;
    if (salesCommission.type === 'percentage') {
      salesCommissionAmount = (totalWithVat * (salesCommission.percentage || 0)) / 100;
    } else {
      salesCommissionAmount = salesCommission.amount || 0;
    }
    
    // คำนวณยอดคงเหลือก่อนจ่าย Developer
    const calculatedRemainingBeforeDev = calculatedActualReceived - companyFeeAmount - salesCommissionAmount;
    
    // อัปเดต JSON fields ด้วย calculated values
    const updatedVat = {
      ...projectFields.vat,
      amount: calculatedVatAmount
    };
    
    const updatedWithholdingTax = {
      ...projectFields.withholding_tax,
      amount: calculatedWithholdingTaxAmount
    };
    
    const updatedCompanyFee = {
      ...projectFields.company_fee,
      amount: companyFeeAmount
    };
    
    const updatedSalesCommission = {
      ...projectFields.sales_commission,
      amount: salesCommissionAmount
    };
    
    // อัปเดต projectFields ด้วย calculated values
    const updatedProjectFields = {
      ...projectFields,
      vat: updatedVat,
      withholding_tax: updatedWithholdingTax,
      company_fee: updatedCompanyFee,
      sales_commission: updatedSalesCommission,
      price_before_vat: calculatedPriceBeforeVat,
      total_with_vat: totalWithVat,
      actual_received: calculatedActualReceived,
      remaining_before_dev: calculatedRemainingBeforeDev,
      total_company_fee_amount: companyFeeAmount,
      total_sales_commission_amount: salesCommissionAmount,
      total_distributed_company_fee: 0, // เริ่มต้นที่ 0
      total_distributed_sales_commission: 0, // เริ่มต้นที่ 0
      final_amount: totalWithVat
    };
    
    console.log('Updated project fields with calculated values:', updatedProjectFields);
    
    // ตรวจสอบว่ามีข้อมูลที่จำเป็นหรือไม่
    if (!updatedProjectFields.project_name || !updatedProjectFields.client_name) {
      console.error('Missing required fields:', {
        project_name: updatedProjectFields.project_name,
        client_name: updatedProjectFields.client_name
      });
      throw new Error('Missing required fields: project_name and client_name');
    }

    console.log('Inserting project to database with fields:', JSON.stringify(updatedProjectFields, null, 2));
    
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .insert([updatedProjectFields])
      .select()
      .single();

    if (projectError) {
      console.error('Error inserting project:', projectError);
      console.error('Project fields that failed:', updatedProjectFields);
      throw projectError;
    }
    
    console.log('Project created successfully:', project);
    console.log('Project ID:', project.id);

    // สร้าง payment installments
    const paymentData = projectData.payment_schedule || [];
    console.log('Processing payment installments:', paymentData);
    if (paymentData.length > 0) {
      const installments = paymentData.map(payment => ({
        project_id: project.id,
        installment_number: payment.installment_number || payment.installment,
        amount: payment.installment_amount || payment.amount,
        percentage: payment.percentage,
        type: payment.type,
        due_date: cleanDateValue(payment.payment_date || payment.due_date),
        paid: payment.paid || false,
        paid_at: cleanDateValue(payment.paid_at),
        payment_channel: mapPaymentChannel(payment.channel || payment.payment_channel)
      }));

      console.log('Payment installments to insert:', installments);

      const { error: installmentsError } = await supabase
        .from('payment_installments')
        .insert(installments);

      if (installmentsError) {
        console.error('Error inserting payment installments:', installmentsError);
        console.error('Installments data that failed:', installments);
        throw installmentsError;
      }
      console.log('Payment installments created successfully');
    }

    // สร้าง developer payments
    if (projectData.developer_payments && projectData.developer_payments.length > 0) {
      console.log('Processing developer payments:', projectData.developer_payments);
      const developers = projectData.developer_payments.map(dev => ({
        project_id: project.id,
        developer_name: dev.name,
        amount: dev.amount,
        percentage: dev.percentage,
        type: dev.type || 'amount', // Default to 'amount' if type is null/undefined
        withholding_tax: dev.withholding_tax || { amount: 0, enabled: true, percentage: 3 },
        net_amount: dev.net_amount || 0,
        payment_mode: dev.payment_mode || 'after_completion',
        payment_date: cleanDateValue(dev.payment_date),
        paid: dev.paid || false,
        payment_channel: mapPaymentChannel(dev.payment_channel)
      }));

      console.log('Developer payments to insert:', developers);

      const { error: developersError } = await supabase
        .from('developer_payments')
        .insert(developers);

      if (developersError) {
        console.error('Error inserting developer payments:', developersError);
        console.error('Developers data that failed:', developers);
        throw developersError;
      }
      console.log('Developer payments created successfully');
    }

    // สร้าง other expenses
    if (projectData.other_expenses && projectData.other_expenses.length > 0) {
      console.log('Processing other expenses:', projectData.other_expenses);
      const expenses = projectData.other_expenses.map(expense => ({
        project_id: project.id,
        expense_name: expense.name,
        amount: expense.amount,
        description: expense.description,
        expense_date: cleanDateValue(expense.date),
        paid: expense.paid || false,
        paid_at: cleanDateValue(expense.paid_at),
        payment_channel: mapPaymentChannel(expense.payment_channel)
      }));

      console.log('Other expenses to insert:', expenses);

      const { error: expensesError } = await supabase
        .from('other_expenses')
        .insert(expenses);

      if (expensesError) {
        console.error('Error inserting other expenses:', expensesError);
        console.error('Expenses data that failed:', expenses);
        throw expensesError;
      }
      console.log('Other expenses created successfully');
    }

    console.log('Project creation completed successfully');
    return { data: project, error: null };
  } catch (error) {
    console.error('Error creating project:', error);
    console.error('Error stack:', error.stack);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับอัปเดตโปรเจค
export const updateProject = async (id, projectData) => {
  try {
    console.log('updateProject called with id:', id, 'data:', projectData);
    
    // กรองข้อมูลที่ไม่ควรส่งไปยัง projects table
    const { 
      payment_schedule, 
      developer_payments,
      other_expenses,
      // กรอง calculated fields ที่ไม่มีใน database schema
      priceBeforeVat,
      vatAmount,
      finalAmount,
      withholdingTaxAmount,
      actualReceived,
      actualReceivedAmount, // เพิ่ม field นี้
      totalPaidAmount, // เพิ่ม field นี้
      totalPaymentAmount, // เพิ่ม field นี้
      totalDevPaymentsAmount,
      totalOtherExpensesAmount,
      companyFeeAmount, // เพิ่ม field นี้
      salesCommissionAmount, // เพิ่ม field นี้
      paidInstallmentsCount, // เพิ่ม field นี้
      totalInstallmentsCount, // เพิ่ม field นี้
      profit, // เพิ่ม field นี้
      totalCompanyFeeAmount,
      totalSalesCommissionAmount,
      remainingBeforeDev,
      discountAmount, // เพิ่มฟิลด์นี้
      discountPercentage, // เพิ่มฟิลด์นี้
      projectPrice, // เพิ่มฟิลด์นี้
      priceAfterDiscount, // เพิ่มฟิลด์นี้
      vatPercentage, // เพิ่มฟิลด์นี้
      withholdingTaxPercentage, // เพิ่มฟิลด์นี้
      ...projectFields 
    } = projectData;

    console.log('Project fields to update:', projectFields);
    console.log('Payment schedule data:', payment_schedule);
    console.log('Developer payments data:', developer_payments);
    console.log('Other expenses data:', other_expenses);

    // คำนวณค่า calculated fields ก่อนบันทึก (เหมือนกับ createProject)
    const updateProjectPrice = parseFloat(projectFields.project_price) || 0;
    const updateVatEnabled = projectFields.vat?.enabled || false;
    const updateVatPercentage = projectFields.vat?.percentage || 0;
    const updateWithholdingTaxEnabled = projectFields.withholding_tax?.enabled || false;
    const updateWithholdingTaxConfig = projectFields.withholding_tax || {};
    
    // คำนวณส่วนลด
    const updateDiscount = projectFields.discount || {};
    let updateCalculatedDiscountAmount = 0;
    if (updateDiscount.enabled) {
      if (updateDiscount.type === 'percentage') {
        updateCalculatedDiscountAmount = (updateProjectPrice * (updateDiscount.percentage || 0)) / 100;
      } else {
        updateCalculatedDiscountAmount = updateDiscount.amount || 0;
      }
    }
    
    // คำนวณราคาหลังหักส่วนลด
    const updatePriceAfterDiscount = updateProjectPrice - updateCalculatedDiscountAmount;
    
    // คำนวณราคาไม่รวม VAT และราคารวม VAT ตาม price_type
    let updatePriceBeforeVat = 0;
    let updateTotalWithVat = 0;
    let updateVatAmount = 0;
    
    if (projectFields.price_type === 'include_vat') {
      // ราคารวม VAT แล้ว - แยก VAT ออกมา
      updateTotalWithVat = updatePriceAfterDiscount;
      updatePriceBeforeVat = updateVatEnabled ? updateTotalWithVat / (1 + (updateVatPercentage / 100)) : updateTotalWithVat;
      updateVatAmount = updateVatEnabled ? updateTotalWithVat - updatePriceBeforeVat : 0;
    } else {
      // ราคาไม่รวม VAT - คำนวณ VAT เพิ่ม
      updatePriceBeforeVat = updatePriceAfterDiscount;
      updateVatAmount = updateVatEnabled ? (updatePriceBeforeVat * updateVatPercentage) / 100 : 0;
      updateTotalWithVat = updatePriceBeforeVat + updateVatAmount;
    }
    
    // คำนวณ WHT ตามประเภทที่เลือก
    let updateWithholdingTaxAmount = 0;
    if (updateWithholdingTaxEnabled) {
      if (updateWithholdingTaxConfig.type === 'amount') {
        // ใช้จำนวนเงินที่กรอกโดยตรง
        updateWithholdingTaxAmount = updateWithholdingTaxConfig.amount || 0;
      } else {
        // คำนวณจากเปอร์เซ็นต์
        const updateWithholdingTaxPercentage = updateWithholdingTaxConfig.percentage || 0;
        updateWithholdingTaxAmount = (updatePriceBeforeVat * updateWithholdingTaxPercentage / 100);
      }
    }
    
    // คำนวณยอดรับจริง (ราคารวม VAT - WHT)
    const updateActualReceived = updateTotalWithVat - updateWithholdingTaxAmount;
    
    // คำนวณค่าบริการบริษัท (ใช้ราคารวม VAT เหมือนกับ form)
    const updateCompanyFee = projectFields.company_fee || {};
    let updateCompanyFeeAmount = 0;
    if (updateCompanyFee.type === 'percentage') {
      updateCompanyFeeAmount = (updateTotalWithVat * (updateCompanyFee.percentage || 0)) / 100;
    } else {
      updateCompanyFeeAmount = updateCompanyFee.amount || 0;
    }
    
    // คำนวณค่าคอมมิชชั่น (ใช้ราคารวม VAT เหมือนกับ form)
    const updateSalesCommission = projectFields.sales_commission || {};
    let updateSalesCommissionAmount = 0;
    if (updateSalesCommission.type === 'percentage') {
      updateSalesCommissionAmount = (updateTotalWithVat * (updateSalesCommission.percentage || 0)) / 100;
    } else {
      updateSalesCommissionAmount = updateSalesCommission.amount || 0;
    }
    
    // คำนวณยอดคงเหลือก่อนจ่าย Developer
    const updateRemainingBeforeDev = updateActualReceived - updateCompanyFeeAmount - updateSalesCommissionAmount;
    
    // อัปเดต JSON fields ด้วย calculated values
    const updateUpdatedVat = {
      ...projectFields.vat,
      amount: updateVatAmount
    };
    
    const updateUpdatedWithholdingTax = {
      ...projectFields.withholding_tax,
      amount: updateWithholdingTaxAmount
    };
    
    const updateUpdatedCompanyFee = {
      ...projectFields.company_fee,
      amount: updateCompanyFeeAmount
    };
    
    const updateUpdatedSalesCommission = {
      ...projectFields.sales_commission,
      amount: updateSalesCommissionAmount
    };
    
    // อัปเดต projectFields ด้วย calculated values
    const updatedProjectFields = {
      ...projectFields,
      vat: updateUpdatedVat,
      withholding_tax: updateUpdatedWithholdingTax,
      company_fee: updateUpdatedCompanyFee,
      sales_commission: updateUpdatedSalesCommission,
      price_before_vat: updatePriceBeforeVat,
      total_with_vat: updateTotalWithVat,
      actual_received: updateActualReceived,
      remaining_before_dev: updateRemainingBeforeDev,
      total_company_fee_amount: updateCompanyFeeAmount,
      total_sales_commission_amount: updateSalesCommissionAmount,
      final_amount: updateTotalWithVat
    };
    
    console.log('Updated project fields with calculated values:', updatedProjectFields);

    // อัปเดตข้อมูลโปรเจคหลัก
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .update(updatedProjectFields)
      .eq('id', id)
      .select()
      .single();

    if (projectError) {
      console.error('Error updating project:', projectError);
      throw projectError;
    }
    
    console.log('Project updated successfully:', project);

    // ลบ payment installments เก่า
    console.log('Deleting old payment installments...');
    const { error: deleteInstallmentsError } = await supabase
      .from('payment_installments')
      .delete()
      .eq('project_id', id);

    if (deleteInstallmentsError) {
      console.error('Error deleting old payment installments:', deleteInstallmentsError);
      throw deleteInstallmentsError;
    }
    console.log('Old payment installments deleted successfully');

    // สร้าง payment installments ใหม่
    const paymentData = projectData.payment_schedule || [];
    console.log('Processing payment installments for update:', paymentData);
    if (paymentData.length > 0) {
      const installments = paymentData.map(payment => ({
        project_id: id,
        installment_number: payment.installment_number || payment.installment,
        amount: payment.installment_amount || payment.amount,
        percentage: payment.percentage,
        type: payment.type,
        due_date: cleanDateValue(payment.payment_date || payment.due_date),
        paid: payment.paid || false,
        paid_at: cleanDateValue(payment.paid_at),
        payment_channel: mapPaymentChannel(payment.channel || payment.payment_channel)
      }));

      console.log('Payment installments to insert:', installments);

      const { error: installmentsError } = await supabase
        .from('payment_installments')
        .insert(installments);

      if (installmentsError) {
        console.error('Error inserting payment installments:', installmentsError);
        throw installmentsError;
      }
      console.log('Payment installments updated successfully');
    }

    // ลบ developer payments เก่า
    console.log('Deleting old developer payments...');
    const { error: deleteDevelopersError } = await supabase
      .from('developer_payments')
      .delete()
      .eq('project_id', id);

    if (deleteDevelopersError) {
      console.error('Error deleting old developer payments:', deleteDevelopersError);
      throw deleteDevelopersError;
    }
    console.log('Old developer payments deleted successfully');

    // สร้าง developer payments ใหม่
    if (projectData.developer_payments && projectData.developer_payments.length > 0) {
      console.log('Processing developer payments for update:', projectData.developer_payments);
      const developers = projectData.developer_payments.map(dev => ({
        project_id: id,
        developer_name: dev.name,
        amount: dev.amount,
        percentage: dev.percentage,
        type: dev.type || 'amount', // Default to 'amount' if type is null/undefined
        withholding_tax: dev.withholding_tax || { amount: 0, enabled: true, percentage: 3 },
        net_amount: dev.net_amount || 0,
        payment_mode: dev.payment_mode || 'after_completion',
        payment_date: cleanDateValue(dev.payment_date),
        paid: dev.paid || false,
        payment_channel: mapPaymentChannel(dev.payment_channel)
      }));

      console.log('Developer payments to insert:', developers);

      const { error: developersError } = await supabase
        .from('developer_payments')
        .insert(developers);

      if (developersError) {
        console.error('Error inserting developer payments:', developersError);
        throw developersError;
      }
      console.log('Developer payments updated successfully');
    }

    // ลบ other expenses เก่า
    console.log('Deleting old other expenses...');
    const { error: deleteExpensesError } = await supabase
      .from('other_expenses')
      .delete()
      .eq('project_id', id);

    if (deleteExpensesError) {
      console.error('Error deleting old other expenses:', deleteExpensesError);
      throw deleteExpensesError;
    }
    console.log('Old other expenses deleted successfully');

    // สร้าง other expenses ใหม่
    if (projectData.other_expenses && projectData.other_expenses.length > 0) {
      console.log('Processing other expenses for update:', projectData.other_expenses);
      const expenses = projectData.other_expenses.map(expense => ({
        project_id: id,
        expense_name: expense.name,
        amount: expense.amount,
        description: expense.description,
        expense_date: cleanDateValue(expense.date),
        paid: expense.paid || false,
        paid_at: cleanDateValue(expense.paid_at),
        payment_channel: mapPaymentChannel(expense.payment_channel)
      }));

      console.log('Other expenses to insert:', expenses);

      const { error: expensesError } = await supabase
        .from('other_expenses')
        .insert(expenses);

      if (expensesError) {
        console.error('Error inserting other expenses:', expensesError);
        throw expensesError;
      }
      console.log('Other expenses updated successfully');
    }

    console.log('Project update completed successfully');
    return { data: project, error: null };
  } catch (error) {
    console.error('Error updating project:', error);
    console.error('Error stack:', error.stack);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับลบโปรเจค
export const deleteProject = async (id) => {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting project:', error);
    return { error };
  }
};

// ฟังก์ชันสำหรับอัปเดตสถานะการชำระเงิน
export const updatePaymentStatus = async (projectId, installmentId, paid) => {
  try {
    const { data, error } = await supabase
      .from('payment_installments')
      .update({ 
        paid,
        paid_at: paid ? new Date().toISOString() : null
      })
      .eq('id', installmentId)
      .eq('project_id', projectId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating payment status:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับอัปเดตสถานะการจ่ายค่าจ้าง Developer
export const updateDeveloperPaymentStatus = async (projectId, developerId, paid) => {
  try {
    const { data, error } = await supabase
      .from('developer_payments')
      .update({ 
        paid,
        payment_date: paid ? new Date().toISOString() : null
      })
      .eq('id', developerId)
      .eq('project_id', projectId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating developer payment status:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับอัปเดตสถานะการจ่ายค่าใช้จ่ายอื่นๆ
export const updateOtherExpenseStatus = async (projectId, expenseId, paid) => {
  try {
    const { data, error } = await supabase
      .from('other_expenses')
      .update({ 
        paid,
        paid_at: paid ? new Date().toISOString() : null
      })
      .eq('id', expenseId)
      .eq('project_id', projectId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating other expense status:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับอัปเดตข้อมูลการชำระเงิน
export const updatePaymentData = async (projectId, installmentId, updateData) => {
  try {
    const { data, error } = await supabase
      .from('payment_installments')
      .update({
        due_date: cleanDateValue(updateData.due_date),
        payment_channel: mapPaymentChannel(updateData.payment_channel)
      })
      .eq('id', installmentId)
      .eq('project_id', projectId)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating payment data:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับอัปเดตข้อมูล Developer
export const updateDeveloperData = async (projectId, developerId, updateData) => {
  try {
    const { data, error } = await supabase
      .from('developer_payments')
      .update({
        developer_name: updateData.developer_name,
        payment_date: cleanDateValue(updateData.payment_date)
      })
      .eq('id', developerId)
      .eq('project_id', projectId)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating developer data:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับอัปเดตข้อมูลค่าใช้จ่ายอื่นๆ
export const updateExpenseData = async (projectId, expenseId, updateData) => {
  try {
    const { data, error } = await supabase
      .from('other_expenses')
      .update({
        expense_name: updateData.expense_name,
        amount: updateData.amount,
        description: updateData.description,
        expense_date: cleanDateValue(updateData.expense_date),
        payment_channel: mapPaymentChannel(updateData.payment_channel)
      })
      .eq('id', expenseId)
      .eq('project_id', projectId)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating expense data:', error);
    return { data: null, error };
  }
};

// ฟังก์ชันสำหรับดึงสถิติโปรเจค
export const getProjectStats = async () => {
  try {
    // ดึงข้อมูลโปรเจค
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('final_amount');

    if (projectsError) throw projectsError;

    // ดึงข้อมูล payment installments
    const { data: payments, error: paymentsError } = await supabase
      .from('payment_installments')
      .select('project_id, amount, paid');

    if (paymentsError) throw paymentsError;

    const stats = projects.reduce((acc, project) => {
      const projectPayments = payments?.filter(payment => payment.project_id === project.id) || [];
      const totalPaid = projectPayments
        .filter(payment => payment.paid)
        .reduce((sum, payment) => sum + payment.amount, 0);

      return {
        totalProjects: acc.totalProjects + 1,
        totalRevenue: acc.totalRevenue + project.final_amount,
        totalPaid: acc.totalPaid + totalPaid,
        pendingAmount: acc.pendingAmount + (project.final_amount - totalPaid)
      };
    }, {
      totalProjects: 0,
      totalRevenue: 0,
      totalPaid: 0,
      pendingAmount: 0
    });

    return { data: stats, error: null };
  } catch (error) {
    console.error('Error fetching project stats:', error);
    return { data: null, error };
  }
};
